import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { VisualInspectionDto, VisualInspectionGetPaginatedDto } from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type VisualInspectionAutocompleteOption = BaseAutocompleteOption<VisualInspectionDto>;

export const getVisualInspectionOptionTitle = (data: VisualInspectionDto): string =>
  StringHelper.join(
    [data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber],
    ", ",
    {
      skipEmpty: true,
    },
  );

export const visualInspectionToOption = (
  data: VisualInspectionDto,
): VisualInspectionAutocompleteOption => ({
  id: data.id!,
  title: getVisualInspectionOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface VisualInspectionAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<VisualInspectionDto> {
  searchFilters?: Partial<Omit<VisualInspectionGetPaginatedDto, "search" | "includeIds">>;
}

export default function VisualInspectionAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: VisualInspectionAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={visualInspectionToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.visualInspectionsApi.apiV1VisualInspectionsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          visualInspectionGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          visualInspectionGetPaginatedDto: {
            ...params.visualInspectionGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Visual inspection'
      placeholder='Search...'
      renderOption={(props, option: VisualInspectionAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='visualInspection' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                      {option?.data?.contract && (
                        <ContractInline entity={option.data.contract} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
